import React, { memo, useEffect, useState } from "react";
import StarterKit from "@tiptap/starter-kit";
import { useEditor, EditorContent } from "@tiptap/react";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import CharacterCount from "@tiptap/extension-character-count";
import TextAlign from "@tiptap/extension-text-align";
import { Accordion, AccordionDetails, Box } from "@mui/material";

import EditorToolbar from "./EditorToolbar";
import { ContentsType, LanguagesType } from "../interfaces";
import { isValidLinkHref, MAX_CHARACTERS } from "../utils";
import EditorAccordionSummary from "./EditorAccordionSummary";

const EditorContainer = memo(
  ({
    collapseAll,
    editorErrors,
    errorFilledMsg,
    lang,
    onContentsChange,
  }: {
    collapseAll: boolean;
    editorErrors?: string[];
    errorFilledMsg?: string;
    lang: LanguagesType;
    onContentsChange: (contents: ContentsType) => void;
  }) => {
    const [charCount, setCharCount] = useState(0);
    const [collapse, setCollapse] = useState(collapseAll);

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          strike: false,
          code: false,
          blockquote: false,
          horizontalRule: false,
          codeBlock: false,
          link: false,
          underline: false,
        }),
        Underline,
        Link.configure({
          openOnClick: true,
          HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
          shouldAutoLink: (href) => isValidLinkHref(href),
          validate: (href) => isValidLinkHref(href),
        }),
        CharacterCount.configure({
          limit: MAX_CHARACTERS,
        }),
        TextAlign.configure({
          types: ["heading", "paragraph"],
          alignments: ["left", "center", "right", "justify"],
        }),
      ],
      enableContentCheck: true,
      editorProps: {
        attributes: {
          class: "tiptap--container",
        },
      },
      onBlur: ({ editor }) => {
        const json = editor.getJSON();
        setCharCount(editor.storage.characterCount.characters());
        onContentsChange({ id: lang.id, content: json });
      },
    });

    useEffect(() => {
      setCollapse(collapseAll);
    }, [collapseAll]);

    return (
      <Accordion
        expanded={collapse}
        disableGutters={true}
        defaultExpanded={false}
        onChange={(_, expanded) => {
          setCollapse(expanded);
        }}
        square
        sx={(theme) => ({
          backgroundColor: "unset",
          boxShadow: "unset",
          borderBottom: `2px solid ${theme.vars.palette.divider}`,
          py: 0.5,
          "& .MuiButtonBase-root.MuiAccordionSummary-root": {
            minHeight: 0,
            "& span": {
              margin: 0,
            },
          },
          "&:before": {
            content: "unset",
          },
          "&.Mui-expanded": {
            marginTop: 0,
          },
        })}
      >
        <EditorAccordionSummary
          charCount={charCount}
          editorErrors={editorErrors}
          errorFilledMsg={errorFilledMsg}
          lang={lang}
        />
        <AccordionDetails>
          <Box sx={{ width: "100%" }}>
            <Box
              sx={(theme) => ({
                display: "block",
                borderRadius: "6px",
                border: `1px solid ${theme.vars.palette.divider}`,
                "& .tiptap--container": {
                  backgroundColor: "background.input",
                  py: 0.5,
                  px: 0.75,
                  outline: "unset",
                  minHeight: "50px",
                  maxHeight: "150px",
                  overflowY: "auto",
                  "& p, & h1, & h2, & h3, & h4, & h5, & h6": {
                    margin: 0,
                  },
                },
              })}
            >
              <EditorToolbar editor={editor} />
              <EditorContent editor={editor} />
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  }
);

export default EditorContainer;
